import React, { useRef, useState, useEffect } from "react";
import { useElementScrollProgress } from "../../hooks/useScrollProgress";
import { Link } from "react-router-dom";

const LETTERS = [
  { letter: "S", word: "System" },
  { letter: "A", word: "Adaptive" },
  { letter: "N", word: "Neural" },
  { letter: "K", word: "Knowledge" },
  { letter: "H", word: "Hiring" },
  { letter: "Y", word: "Yield" },
  { letter: "A", word: "Analysis" },
];

export function SankhyaHero() {
  const heroRef = useRef(null);
  const progress = useElementScrollProgress(heroRef);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-[180vh]"
    >
      <div className="sticky top-0 h-screen w-full flex flex-col items-center justify-center overflow-hidden">
        {/* Background gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(
              ${135 + progress * 45}deg,
              oklch(0.96 0.03 280),
              oklch(0.95 0.025 160 / ${0.6 + progress * 0.3}),
              oklch(0.97 0.02 90 / ${0.4 + progress * 0.5})
            )`,
          }}
        />

        {/* Letter reveal */}
        <div className="relative z-10 flex items-start justify-center gap-3 md:gap-5">
          {LETTERS.map(({ letter, word }, i) => (
            <div
              key={`${letter}-${i}`}
              className="flex flex-col items-center"
              style={{
                width: "clamp(2.8rem, 8vw, 7rem)",
                opacity: mounted ? 1 : 0,
                transform: mounted ? "translateY(0)" : "translateY(30px)",
                transition: `opacity 0.7s ease-out ${i * 100 + 200}ms, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${i * 100 + 200}ms`,
              }}
            >
              <span
                className="font-bold tracking-widest select-none block text-center"
                style={{
                  fontSize: "clamp(2.5rem, 8vw, 7rem)",
                  color: "oklch(0.25 0.03 280)",
                }}
              >
                {letter}
              </span>
              <span
                className="text-[9px] md:text-[11px] tracking-[0.15em] uppercase font-light mt-1 block text-center whitespace-nowrap"
                style={{
                  color: "oklch(0.5 0.04 280)",
                  opacity: mounted ? 1 : 0,
                  transition: `opacity 0.5s ease-out ${i * 100 + 600}ms`,
                }}
              >
                {word}
              </span>
            </div>
          ))}
        </div>

        {/* Subtitle */}
        <div
          className="relative z-10 mt-10 text-center px-6"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.8s ease-out 1.2s, transform 0.8s ease-out 1.2s",
          }}
        >
          <p
            className="text-sm md:text-lg tracking-[0.2em] uppercase font-light max-w-2xl"
            style={{ color: "oklch(0.4 0.04 280)" }}
          >
            System for Adaptive Neural Knowledge, Hiring & Yield Analysis
          </p>
          <p
            className="mt-4 text-xs md:text-sm tracking-[0.12em] italic"
            style={{
              color: "oklch(0.5 0.04 160)",
              opacity: mounted ? 1 : 0,
              transition: "opacity 0.8s ease-out 1.6s",
            }}
          >
            Sankhya — the philosophy of pure logic
          </p>
          
          <div className="mt-12 flex justify-center gap-4">
             <Link to="/dashboard">
                <button className="px-8 py-3 rounded-full bg-black text-white text-xs tracking-widest uppercase hover:bg-zinc-800 transition-all font-medium">
                  Enter Dashboard
                </button>
             </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className="absolute bottom-10 flex flex-col items-center gap-2"
          style={{
            opacity: mounted ? Math.max(0, 1 - progress * 4) : 0,
            transition: "opacity 0.5s ease-out 2s",
          }}
        >
          <span className="text-xs tracking-[0.25em] uppercase" style={{ color: "oklch(0.55 0.03 280)" }}>
            Scroll to discover
          </span>
          <div className="w-px h-8 sankhya-scroll-line" />
        </div>
      </div>
    </section>
  );
}
